import { useEffect, useState, useCallback } from "react";
import { Trash2, RotateCcw, Users, MicOff } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { roleFor, nameColor } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/RoleBadge";
import { SectionTitle, Empty } from "@/admin/ui";
import { toast } from "sonner";

function MsgRow({ m, onDelete, onChatBan, roles }) {
  const role = roleFor(m, roles);
  return (
    <div data-testid="admin-chat-message" className={`px-4 py-2.5 flex gap-3 text-sm ${m.cleared ? "opacity-50" : ""}`}>
      <span className="text-[10px] text-slate-600 font-mono-cx shrink-0 w-28">{fmtDate(m.created_at)}</span>
      <div className="min-w-0 flex-1">
        <span className="text-xs font-bold" style={{ color: m.role === "system" ? "#22d3ee" : nameColor(m, roles) }}>{m.username}</span> {role && <RoleBadge role={role} size="xs" />}
        <p className="text-slate-200 break-words">{m.text}</p>
      </div>
      {onChatBan && m.user_id && m.role !== "admin" && <button data-testid="admin-chat-ban-from-log" title="Sohbetten banla" onClick={() => onChatBan(m)} className="text-slate-600 hover:text-amber-300 shrink-0"><MicOff className="h-3.5 w-3.5" /></button>}
      {onDelete && <button data-testid="admin-delete-message" onClick={() => onDelete(m)} className="text-slate-600 hover:text-red-300 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

export function AdminChats({ initialUser }) {
  const { roles } = useAuth();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(initialUser?.id || "public");
  const [publicMsgs, setPublicMsgs] = useState(null);
  const [userData, setUserData] = useState(null);
  const [conv, setConv] = useState(null);
  const [convMsgs, setConvMsgs] = useState([]);

  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role !== "admin"))).catch(() => {}); }, []);

  const load = useCallback(() => {
    setConv(null); setConvMsgs([]);
    if (selected === "public") { setUserData(null); api.get("/admin/chat/public").then((r) => setPublicMsgs(r.data)).catch(() => setPublicMsgs([])); }
    else { setPublicMsgs(null); api.get(`/admin/users/${selected}/conversations`).then((r) => setUserData(r.data)).catch(() => setUserData({ conversations: [], public_messages: [] })); }
  }, [selected]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!conv) return;
    api.get(`/admin/conversations/${conv.id}/messages`).then((r) => setConvMsgs(r.data)).catch(() => {});
  }, [conv]);

  const clearChat = async () => {
    if (!window.confirm("Genel sohbet herkes için sıfırlansın mı? (Kayıtlar admin panelde kalır)")) return;
    try { await api.post("/admin/chat/clear"); toast.success("Sohbet sıfırlandı"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (m) => {
    try { await api.delete(`/admin/chat/messages/${m.id}`); load(); if (conv) setConvMsgs((p) => p.filter((x) => x.id !== m.id)); } catch (e) { toast.error(errMsg(e)); }
  };
  const chatBan = async (m) => {
    if (!window.confirm(`${m.username} sohbetten banlansın mı?`)) return;
    try { await api.post(`/admin/users/${m.user_id}/chat-ban`, { chat_banned: true }); toast.success(`${m.username} sohbetten banlandı`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const selUser = users.find((u) => u.id === selected);

  return (
    <div>
      <SectionTitle eyebrow="Moderasyon" title="Sohbet Kayıtları">
        <div className="flex gap-2 flex-wrap">
          <select data-testid="admin-chat-user-select" value={selected} onChange={(e) => setSelected(e.target.value)} className="h-10 rounded-xl bg-slate-900 border border-slate-700 text-sm text-slate-200 px-3 min-w-[220px]">
            <option value="public">Genel Sohbet (tüm kayıtlar)</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select>
          {selected === "public" && <button data-testid="admin-clear-chat-button" onClick={clearChat} className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-bold inline-flex items-center gap-2 transition-colors"><RotateCcw className="h-4 w-4" /> Sohbet Geçmişini Sıfırla</button>}
        </div>
      </SectionTitle>

      {selected === "public" && (
        publicMsgs === null ? <div className="h-40 rounded-2xl bg-slate-900/60 animate-pulse" /> : publicMsgs.length === 0 ? <Empty text="Henüz mesaj yok." /> : (
          <div className="cx-glass rounded-2xl divide-y divide-slate-800/60 max-h-[70vh] overflow-y-auto" data-testid="admin-public-chat-log">
            {publicMsgs.map((m) => <MsgRow key={m.id} m={m} roles={roles} onDelete={del} onChatBan={chatBan} />)}
          </div>
        )
      )}

      {selected !== "public" && userData && (
        <div className="grid lg:grid-cols-[320px_1fr] gap-4">
          <div className="space-y-3">
            <div className="cx-glass rounded-2xl p-4">
              <p className="font-mono-cx text-[10px] uppercase tracking-widest text-slate-500 mb-2 inline-flex items-center gap-1"><Users className="h-3 w-3" /> {selUser?.username} — Özel Sohbetleri</p>
              {userData.conversations.length === 0 && <p className="text-xs text-slate-600">Özel sohbeti yok.</p>}
              {userData.conversations.map((c) => (
                <button key={c.id} data-testid="admin-conversation-item" onClick={() => setConv(c)} className={`w-full text-left rounded-xl border p-3 mb-2 transition-colors ${conv?.id === c.id ? "border-cyan-500/60 bg-slate-800" : "border-slate-800 bg-slate-900/60 hover:border-slate-600"}`}>
                  <div className="flex justify-between text-sm"><span className="font-semibold text-slate-100">{c.other_username}</span><span className="text-[10px] text-slate-500 font-mono-cx">{c.message_count} mesaj</span></div>
                  <p className="text-xs text-slate-500 truncate mt-1">{c.last_message || "—"}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="cx-glass rounded-2xl max-h-[70vh] overflow-y-auto">
            {conv ? (
              <>
                <div className="px-4 py-3 border-b border-slate-800 text-sm font-semibold text-slate-100">{selUser?.username} ↔ {conv.other_username}</div>
                <div className="divide-y divide-slate-800/60" data-testid="admin-conversation-messages">{convMsgs.map((m) => <MsgRow key={m.id} m={m} roles={roles} onDelete={del} />)}{convMsgs.length === 0 && <p className="p-6 text-xs text-slate-600">Mesaj yok.</p>}</div>
              </>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-slate-800 text-sm font-semibold text-slate-100">Genel sohbetteki mesajları ({userData.public_messages.length})</div>
                <div className="divide-y divide-slate-800/60" data-testid="admin-user-public-messages">{userData.public_messages.map((m) => <MsgRow key={m.id} m={m} roles={roles} onDelete={del} onChatBan={chatBan} />)}{userData.public_messages.length === 0 && <p className="p-6 text-xs text-slate-600">Mesaj yok.</p>}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
